/**
 * Dashboard team slice schema (#1338 PR A, program #1130 E2)
 *
 * Zod contract for GET /api/v1/dashboard/team — the «Команда» tab: who is
 * fine, who needs help, who grows, how scores spread, who carries the load.
 * Same window and scope rules as the quality slice (`dashboard-quality.ts`):
 * half-open current `[start, end)`, previous `[previousStart, start)` of the
 * same length, the viewer's scope by role or grant.
 *
 * One row per PERSON, keyed like the personal rank (the employee roster in
 * `@aica/core`, #1927): an Aelo account by its user id, a CRM id folded into
 * the account that owns it, a CRM id with no account as
 * `<installation id>:<id>` — so a person with a CRM id and an Aelo id is one
 * row, the same id in two portals is two, and the table agrees with «my
 * position» (owner decisions 09.10, 31.10).
 * Records with no employee count in `unattributed`, never in a row.
 *
 * Scores are the canonical quality score with the project's weights (the E1
 * definition); `null` is «no scored record», never a zero. The thresholds a
 * block applies are exported below and named in the answer's numbers, so the
 * client never decides who «needs help» by itself.
 */

import { z } from 'zod/v4';
import {
  dashboardQualityQuerySchema,
  dashboardQualityScopeSchema,
  dashboardSliceWindowSchema,
} from './dashboard-quality';
import { REPORT_AGENT_MIN_SCORED_CALLS } from './reports';

// ============================================================================
// Thresholds (design 09.10, owner decisions 02.10 / 09.10)
// ============================================================================

/**
 * Below this many scored conversations a person has «few ratings»: no score
 * shown, in no rule. The reports' minimum, not a second one — an employee is
 * «few ratings» on every screen or on none.
 */
export const TEAM_MIN_SCORED = REPORT_AGENT_MIN_SCORED_CALLS;
/** «Кому нужна помощь» — a rated person's score below this (owner 02.10). */
export const TEAM_NEEDS_HELP_BELOW = 70;
/** «Растут» — a change of at least this many points, rated in both windows (owner 09.10). */
export const TEAM_GROWTH_MIN_POINTS = 3;

/**
 * A person's change in points, to one decimal — the one comparison the server's
 * `growing` count and the client's list of names both apply, so 61.1 → 64.1 is
 * 3 on both sides (unrounded, floating point makes it 2.999…).
 */
export function teamChangePoints(current: number, previous: number): number {
  return Math.round((current - previous) * 10) / 10;
}
/**
 * Who a rule picks — the ONE place the server's counts and the client's lists
 * of names read their membership from, so a card's number and its names
 * cannot drift apart. A person here is anything with the two windows' score
 * and scored count (the reader's person, or a row of the answer).
 */
export interface TeamRulePerson {
  current: { score: number | null; scoredCount: number };
  previous: { score: number | null; scoredCount: number };
}

/** Rated: enough scored conversations in the current window for a score to be shown. */
export function isTeamRated(person: TeamRulePerson): boolean {
  return person.current.score !== null && person.current.scoredCount >= TEAM_MIN_SCORED;
}

/** Rated in both windows — the base of a change. */
export function isTeamComparable(person: TeamRulePerson): boolean {
  return (
    isTeamRated(person) &&
    person.previous.score !== null &&
    person.previous.scoredCount >= TEAM_MIN_SCORED
  );
}

/** «Кому нужна помощь»: rated, score below `TEAM_NEEDS_HELP_BELOW`. */
export function teamNeedsHelp(person: TeamRulePerson): boolean {
  return isTeamRated(person) && person.current.score! < TEAM_NEEDS_HELP_BELOW;
}

/** «Растут»: comparable, change of at least `TEAM_GROWTH_MIN_POINTS` (by `teamChangePoints`). */
export function teamIsGrowing(person: TeamRulePerson): boolean {
  return (
    isTeamComparable(person) &&
    teamChangePoints(person.current.score!, person.previous.score!) >=
      TEAM_GROWTH_MIN_POINTS
  );
}

/**
 * An aggregates-only viewer (no `team.scores`): a team of fewer rated people
 * than this shows no team numbers, and a count below `TEAM_MIN_CELL` is
 * withheld (owner 02.10) — a count of 1 in a small team names a person.
 */
export const TEAM_MIN_GROUP = 3;
export const TEAM_MIN_CELL = 2;

/** The five score ranges of the distribution, highest first; every one is answered, empty included. */
export const TEAM_SCORE_RANGES = [
  { range: '90-100', min: 90, max: null },
  { range: '80-89', min: 80, max: 90 },
  { range: '70-79', min: 70, max: 80 },
  { range: '60-69', min: 60, max: 70 },
  { range: '0-59', min: null, max: 60 },
] as const;

// ============================================================================
// Query
// ============================================================================

/** The quality slice's query: the same window, zone and demo setting (`projectIds` read with `getAll`). */
export const dashboardTeamQuerySchema = dashboardQualityQuerySchema;

// ============================================================================
// Response
// ============================================================================

const countSchema = z.number().int().nonnegative();
/** Nominally 0–100, not clamped — see `dashboard-quality.ts`. */
const scoreSchema = z.number();
/**
 * A count an aggregates-only viewer may not see: `null` — withheld
 * (below `TEAM_MIN_CELL`, or the team below `TEAM_MIN_GROUP`), never a zero.
 */
const guardedCountSchema = countSchema.nullable();

export const dashboardTeamEmployeeSchema = z.object({
  /** The person's ranking key — opaque, stable within the answer. */
  key: z.string(),
  /** The Aelo user's name when the key is a user, else the records' name; `null` — none known. */
  name: z.string().nullable(),
  /**
   * The CRM portal of an employee with no Aelo account (`embedded_installs.external_portal_id`),
   * so two rows with the same name from two portals can be told apart (#3021). Never the
   * installation id. Optional and nullable: an answer from a rolled-back or cached edge
   * without it still parses; `null` — an account, an id with no known installation.
   */
  portal: z.string().nullable().optional(),
  current: z.object({
    /** `null` — no scored conversation in the window. */
    score: scoreSchema.nullable(),
    scoredCount: countSchema,
    /** Every conversation of the person in the window, scored or not. */
    conversations: countSchema,
    /** Their total duration, seconds (a conversation without one adds 0). */
    durationSeconds: countSchema,
  }),
  previous: z.object({
    score: scoreSchema.nullable(),
    scoredCount: countSchema,
  }),
});

export const dashboardTeamRangeSchema = z.object({
  range: z.enum(['90-100', '80-89', '70-79', '60-69', '0-59']),
  /** Rated people (n ≥ `TEAM_MIN_SCORED`) whose score falls in the range. */
  count: guardedCountSchema,
});

export const dashboardTeamResponseSchema = z.object({
  metric: z.literal('quality.canonical'),
  metricVersion: z.literal('submetric_mean_v1'),
  source: z.literal('d1'),
  asOf: z.string().datetime(),
  window: dashboardSliceWindowSchema,
  scope: dashboardQualityScopeSchema,
  includeDemo: z.enum(['yes', 'no']),
  team: z.object({
    /** People with at least one conversation in the current window. */
    people: countSchema,
    /** Of them, rated: at least `TEAM_MIN_SCORED` scored conversations. */
    rated: countSchema,
    /** Rated in both windows — the base of «Растут». */
    comparable: countSchema,
    /** Conversations with no employee: in the team's totals, in no row. */
    unattributed: z.object({ conversations: countSchema, scoredCount: countSchema }),
  }),
  /** Rated people with a score below `TEAM_NEEDS_HELP_BELOW`. */
  needsHelp: guardedCountSchema,
  /** Comparable people whose score rose by at least `TEAM_GROWTH_MIN_POINTS`. */
  growing: guardedCountSchema,
  /** Every range of `TEAM_SCORE_RANGES`, in that order, empty ones with 0. */
  distribution: z.array(dashboardTeamRangeSchema).length(TEAM_SCORE_RANGES.length),
  /**
   * The people, busiest first (by minutes, then conversations), at most
   * `DASHBOARD_TEAM_MAX_EMPLOYEES`. Past the cap the rows are not the busiest
   * alone: the lowest scores and the biggest risers the lists print
   * (`TEAM_LIST_ROWS` each) are kept ahead of the rest. Empty when
   * `employeesHidden`.
   */
  employees: z.array(dashboardTeamEmployeeSchema),
  /** More people than rows; the numbers above count all of them. */
  employeesTruncated: z.boolean(),
  /** The viewer's access does not open names and rows (`team.scores`): `employees` is empty by access. */
  employeesHidden: z.boolean(),
  /** An aggregates-only viewer and fewer than `TEAM_MIN_GROUP` rated people: no team numbers. */
  smallGroup: z.boolean(),
});

export const DASHBOARD_TEAM_MAX_EMPLOYEES = 500;
/**
 * How many people the «Нагрузка» card ranks and how many names each of the
 * «needs help» / «improving» lists prints. The rows the route keeps are chosen
 * against these two numbers (`buildTeamNumbers`), so they live here, once.
 */
export const TEAM_WORKLOAD_ROWS = 20;
export const TEAM_LIST_ROWS = 10;

// ============================================================================
// Types
// ============================================================================

export type DashboardTeamQuery = z.infer<typeof dashboardTeamQuerySchema>;
export type DashboardTeamEmployee = z.infer<typeof dashboardTeamEmployeeSchema>;
export type DashboardTeamRange = z.infer<typeof dashboardTeamRangeSchema>;
export type DashboardTeamResponse = z.infer<typeof dashboardTeamResponseSchema>;
